import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import axios from "axios";
import toast from "react-hot-toast";
import Footer from "../../Include/Footer";
import Header from "../../Include/Header";
import Sidebar from "../../Include/Sidebar";
import Loader from "../../Include/Loader";
import { Pagination } from "../../Table";
import PageHeader from "../../Components/PageHeader/PageHeader";
import { V_URL } from "../../../../BaseUrl";
import useDebounce from "../../../../hooks/useDebounce";
import { getProIssueReport } from "../../../../Store/prosafe/proReport/getProIssueReport";
import { getGenMaster } from "../../../../Store/Store/GenralMaster/GenMaster";
import { getProject } from "../../../../Store/Store/Project/Project";
import { getAdminItem } from "../../../../Store/Store/Item/AdminItem";
import { getCategory } from "../../../../Store/Store/StoreMaster/Category/Category";

const ProIssueReport = () => {
    const dispatch = useDispatch();

    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState("");
    const [limit, setLimit] = useState(20);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const [filter, setFilter] = useState({
        startDate: "",
        endDate: "",
        taken_by: [],
        authorised_by: [],
        received_by: [],
        project: null,
        item: null,
        category: null,
    });

    const debouncedSearch = useDebounce(search, 500);
    const debouncedStartDate = useDebounce(filter.startDate, 400);
    const debouncedEndDate = useDebounce(filter.endDate, 400);

    useEffect(() => {
        dispatch(getGenMaster({}));
        dispatch(getProject());
        dispatch(getAdminItem({ is_main: true }));
        dispatch(getCategory());
    }, [dispatch]);

    useEffect(() => {
        fetchReportData();
    }, [
        dispatch,
        debouncedSearch,
        debouncedStartDate,
        debouncedEndDate,
        filter.taken_by,
        filter.authorised_by,
        filter.received_by,
        filter.project,
        filter.item,
        filter.category,
    ]);

    const fetchReportData = () => {
        const payload = {
            firm_id: localStorage.getItem("PAY_USER_FIRM_ID"),
            year_id: localStorage.getItem("PAY_USER_YEAR_ID"),
            start_date: debouncedStartDate || "",
            to_date: debouncedEndDate || "",
            taken_by_id: Array.isArray(filter.taken_by) ? filter.taken_by : (filter.taken_by ? [filter.taken_by] : []),
            authorised_id: Array.isArray(filter.authorised_by) ? filter.authorised_by : (filter.authorised_by ? [filter.authorised_by] : []),
            received_by_id: Array.isArray(filter.received_by) ? filter.received_by : (filter.received_by ? [filter.received_by] : []),
            project_id: filter.project || "",
            item_id: filter.item || "",
            item_category_id: filter.category || "",
            search: debouncedSearch || "",
        };

        dispatch(getProIssueReport(payload));
    };

    const rawReportData = useSelector((state) => state?.getProIssueReport?.user?.data) || [];
    const isLoading = useSelector((state) => state?.getProIssueReport?.loading);

    const genMasterData = useSelector((state) => state?.getGenMaster?.user?.data) || [];
    const projectData = useSelector((state) => state?.getProject?.user?.data) || [];
    const itemData = useSelector((state) => state?.getAdminItem?.user?.data) || [];
    const categoryData = useSelector((state) => state?.getCategory?.user?.data) || [];

    // Dropdown options
    const authByOptions = useMemo(() => {
        return genMasterData
            ?.filter((m) => m.tag_number === 18)
            ?.map((m) => ({ label: m.name, value: m._id }));
    }, [genMasterData]);

    const receivedByOptions = useMemo(() => {
        return genMasterData
            ?.filter((m) => m.tag_number === 19)
            ?.map((m) => ({ label: m.name, value: m._id }));
    }, [genMasterData]);

    const takenByOptions = useMemo(() => {
        return genMasterData
            ?.filter((m) => m.tag_number === 20)
            ?.map((m) => ({ label: m.name, value: m._id }));
    }, [genMasterData]);

    const projectOptions = useMemo(() => {
        return projectData?.map((p) => ({
            label: p.project_no ? `${p.name} (${p.project_no})` : p.name,
            value: p._id,
        }));
    }, [projectData]);

    const itemOptions = useMemo(() => {
        return itemData?.map((it) => ({
            label: it.name,
            value: it._id,
        }));
    }, [itemData]);

    const categoryOptions = useMemo(() => {
        return categoryData?.map((c) => ({
            label: c.name,
            value: c._id,
        }));
    }, [categoryData]);

    const totalItems = Array.isArray(rawReportData) ? rawReportData.length : 0;

    // Paginated list calculation
    const paginatedData = useMemo(() => {
        const data = Array.isArray(rawReportData) ? rawReportData : [];
        return data.slice((currentPage - 1) * limit, (currentPage - 1) * limit + limit);
    }, [rawReportData, currentPage, limit]);

    const handleRefresh = () => {
        setFilter({
            startDate: "",
            endDate: "",
            taken_by: [],
            authorised_by: [],
            received_by: [],
            project: null,
            item: null,
            category: null,
        });
        setSearch("");
        setCurrentPage(1);
    };

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setCurrentPage(1);
    };

    const handleDownloadExcel = async () => {
        try {
            const payload = {
                firm_id: localStorage.getItem("PAY_USER_FIRM_ID"),
                year_id: localStorage.getItem("PAY_USER_YEAR_ID"),
                start_date: filter.startDate || "",
                to_date: filter.endDate || "",
                taken_by_id: Array.isArray(filter.taken_by) ? filter.taken_by : (filter.taken_by ? [filter.taken_by] : []),
                authorised_id: Array.isArray(filter.authorised_by) ? filter.authorised_by : (filter.authorised_by ? [filter.authorised_by] : []),
                received_by_id: Array.isArray(filter.received_by) ? filter.received_by : (filter.received_by ? [filter.received_by] : []),
                project_id: filter.project || "",
                item_id: filter.item || "",
                item_category_id: filter.category || "",
                search: search || "",
            };

            const res = await axios.post(`${V_URL}/prosafe/pro-issue-report-xlsx`, payload, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
                },
            });

            if (res?.data?.success) {
                window.open(res?.data?.data?.url);
                toast.success(res?.data?.message || "Excel generated successfully");
            } else {
                toast.error(res?.data?.message || "Failed to download Excel");
            }
        } catch (err) {
            console.error("Excel download error:", err);
            toast.error(err?.response?.data?.message || "Error generating Excel");
        }
    };

    const handleOpen = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const breadCrumbs = [
        { label: "Dashboard", link: "/main-store/user/dashboard" },
        { label: "Reports" },
        { label: "Issue Report" },
    ];

    const filterConfig = [
        {
            key: "startDate",
            label: "Start Date",
            type: "date",
            value: filter.startDate,
            placeholder: "Select Start Date",
        },
        {
            key: "endDate",
            label: "To Date",
            type: "date",
            value: filter.endDate,
            placeholder: "Select To Date",
        },
        {
            key: "taken_by",
            label: "Taken By",
            type: "multiselect",
            value: filter.taken_by || [],
            options: takenByOptions,
            placeholder: "Select Taken By",
        },
        {
            key: "authorised_by",
            label: "Authorized By",
            type: "multiselect",
            value: filter.authorised_by || [],
            options: authByOptions,
            placeholder: "Select Authorized By",
        },
        {
            key: "received_by",
            label: "Received By",
            type: "multiselect",
            value: filter.received_by || [],
            options: receivedByOptions,
            placeholder: "Select Received By",
        },
        {
            key: "project",
            label: "Project",
            type: "dropdown",
            value: filter.project,
            options: projectOptions,
            placeholder: "Select Project",
            searchable: true,
        },
        {
            key: "item",
            label: "Item",
            type: "dropdown",
            value: filter.item,
            options: itemOptions,
            placeholder: "Select Item",
            searchable: true,
        },
        {
            key: "category",
            label: "Item Category",
            type: "dropdown",
            value: filter.category,
            options: categoryOptions,
            placeholder: "Select Category",
            searchable: true,
        },
    ];

    return (
        <div className={`main-wrapper ${isSidebarOpen ? "slide-nav" : ""}`}>
            <Header handleOpen={handleOpen} />
            <Sidebar />
            <div className="page-wrapper">
                <div className="content">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card card-table show-entire">
                                <div className="card-body">
                                    <PageHeader
                                        title="Issue Report"
                                        breadcrumbs={breadCrumbs}
                                        onSearch={(value) => {
                                            setSearch(value);
                                            setCurrentPage(1);
                                        }}
                                        onRefresh={handleRefresh}
                                        filter={filter}
                                        onFilterChange={handleFilterChange}
                                        limit={limit}
                                        onLimitChange={(val) => {
                                            setLimit(val);
                                            setCurrentPage(1);
                                        }}
                                        filterConfig={filterConfig}
                                        onDownload={handleDownloadExcel}
                                    />

                                    {isLoading ? (
                                        <Loader />
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table border-0 custom-table comman-table  mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Sr.</th>
                                                        <th>Date</th>
                                                        <th>Issue No</th>
                                                        <th style={{ minWidth: "140px", maxWidth: "200px" }}>Receive By</th>
                                                        <th>Authorized By</th>
                                                        <th style={{ minWidth: "140px", maxWidth: "200px" }}>Taken By</th>
                                                        <th>Project</th>
                                                        <th>Item Name</th>
                                                        <th>Req Qty</th>
                                                        <th>Issue Qty</th>
                                                        <th>Remarks</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedData?.map((elem, i) => (
                                                        <tr key={i}>
                                                            <td>{(currentPage - 1) * limit + i + 1}</td>
                                                            <td>
                                                                {elem?.date
                                                                    ? moment(elem.date).format("DD-MM-YYYY")
                                                                    : "-"}
                                                            </td>
                                                            <td>{elem?.issue_no || "-"}</td>
                                                            <td style={{ minWidth: "140px", maxWidth: "200px", whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.4" }}>
                                                                {elem?.receive_by || "-"}
                                                            </td>
                                                            <td>{elem?.authorized_by || "-"}</td>
                                                            <td style={{ minWidth: "140px", maxWidth: "200px", whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.4" }}>
                                                                {elem?.taken_by || "-"}
                                                            </td>
                                                            <td>{elem?.project || "-"}</td>
                                                            <td>{elem?.item_name || "-"}</td>
                                                            <td>{elem?.req_qty ?? "-"}</td>
                                                            <td>{elem?.issue_qty ?? "-"}</td>
                                                            <td style={{ minWidth: "120px", maxWidth: "180px", whiteSpace: "normal", wordBreak: "break-word" }}>
                                                                {elem?.remarks || "-"}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {paginatedData?.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="11">
                                                                <div className="no-table-data">
                                                                    No Data Found!
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ) : null}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    <div className="row align-center mt-3 mb-2">
                                        <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6">
                                            <div className="dataTables_info" id="DataTables_Table_0_info" role="status"
                                                aria-live="polite">Showing {Math.min(limit, totalItems)} from {totalItems} data</div>
                                        </div>
                                        <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6 ">
                                            <div className="dataTables_paginate paging_simple_numbers"
                                                id="DataTables_Table_0_paginate">
                                                <Pagination
                                                    total={totalItems}
                                                    itemsPerPage={limit}
                                                    currentPage={currentPage}
                                                    onPageChange={(page) => setCurrentPage(page)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
};

export default ProIssueReport;
